import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Check, Info, AlertTriangle, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotification } from '../context/NotificationContext';
import { formatDistanceToNow } from 'date-fns';
import { ar } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';

export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, unreadCount, markAsRead } = useNotification();
  const { isAdmin, isInstructor } = useAuth();
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const topNotifications = notifications.slice(0, 5);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const handleNotificationClick = (notif) => {
    if (!notif.isRead) markAsRead(notif.id);
    setIsOpen(false);
    if (notif.link) {
      navigate(notif.link);
    }
  };

  const notificationsLink = isAdmin ? '/admin/notifications' : isInstructor ? '/instructor-dashboard/notifications' : '/dashboard/notifications';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-slate-500 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 rounded-xl transition-all"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-primary-500 rounded-full ring-2 ring-white dark:ring-zinc-950 animate-pulse" />
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute left-0 mt-3 w-80 sm:w-96 bg-zinc-50 dark:bg-zinc-900/80 backdrop-blur-md border border-slate-200 dark:border-zinc-800 rounded-2xl shadow-2xl z-50 overflow-hidden"
            dir="rtl"
          >
            <div className="p-4 border-b border-slate-200 dark:border-zinc-800 flex items-center justify-between bg-white dark:bg-transparent">
              <h3 className="font-black text-slate-900 dark:text-white">الإشعارات</h3>
              {unreadCount > 0 && (
                <span className="bg-primary-500/10 text-primary-600 dark:text-primary-400 text-xs font-bold px-2 py-1 rounded-full">
                  {unreadCount} جديد
                </span>
              )}
            </div>

            <div className="max-h-[350px] overflow-y-auto no-scrollbar">
              {topNotifications.length === 0 ? (
                <div className="p-8 text-center text-slate-500 dark:text-zinc-500">
                  <Bell className="w-10 h-10 mx-auto mb-2 opacity-20" />
                  <p className="font-medium text-sm">لا توجد إشعارات حالياً</p>
                </div>
              ) : (
                <div className="flex flex-col">
                  {topNotifications.map(notif => (
                    <button
                      key={notif.id}
                      onClick={() => handleNotificationClick(notif)}
                      className={`w-full text-right p-4 transition-all duration-300 border-b border-slate-100 dark:border-zinc-800/50 flex gap-4 ${!notif.isRead
                          ? 'bg-slate-50 dark:bg-zinc-800/50 border-r-4 border-r-primary-500'
                          : 'bg-transparent hover:bg-slate-50 dark:hover:bg-zinc-800/20 opacity-75 border-r-4 border-r-transparent'
                        }`}
                    >
                      <div className="shrink-0 mt-1">
                        {getIcon(notif.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-slate-900 dark:text-white text-sm mb-1">{notif.title}</p>
                        <p className="text-xs text-slate-600 dark:text-zinc-400 line-clamp-2">{notif.message}</p>
                        <p className="text-[10px] text-slate-400 dark:text-zinc-500 mt-2 font-medium">
                          {notif.createdAt?.toMillis() ? formatDistanceToNow(notif.createdAt.toMillis(), { addSuffix: true, locale: ar }) : 'الآن'}
                        </p>
                      </div>
                      {!notif.isRead && (
                        <div className="w-2 h-2 rounded-full bg-primary-500 shadow-[0_0_8px_rgba(230,0,35,0.5)] mt-1.5 shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link
              to={notificationsLink}
              onClick={() => setIsOpen(false)}
              className="block flex-1 text-center py-3 text-sm font-bold text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-colors border-t border-slate-200 dark:border-zinc-800 bg-white dark:bg-transparent"
            >
              عرض جميع الإشعارات
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
